// @ts-nocheck

import { isNumber } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import type { Node } from 'react';
import styled from 'styled-components';

import { MatchEntityCompetitors } from '../../states/entities/matches';
import type { ScoreboardV2Props } from '../../states/scoreboards';
import theme from '../../styled-components/dark';
import { MatchTitleAllowedSportIdsAnimation } from '../../types/match';
import { pixelToEm } from '../../utils/common';
import MatchTitle from '../MatchTitle';
import ScoreboardPsoScore from '../ScoreboardPsoScore';
import ScoreboardScorers from '../ScoreboardScorers';

const Wrapper = styled.div`
  width: 100%;
  background-color: ${theme.Scoreboards.wrapperBackground};
  overflow: hidden;
  padding: 0.5em;
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${theme.Scoreboards.color};
`;

const ScoreArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: center;
  margin: 0 ${pixelToEm(3.2)}em;
`;

const SecondaryScore = styled.div`
  color: ${theme.Scoreboards.secondaryScoreColor};
  font-size: ${pixelToEm(10.4)}em;
  white-space: nowrap;
`;

const RowWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const CenterColumn = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
`;

const StyledPsoScore = styled(ScoreboardPsoScore)`
  margin: 0.5em auto 0.2em;
`;

const ScoreboardDefaultLayout = ({
  className,
  competitors,
  scores,
  expandThreshold = 2,
  isCompact = false,
  sportId,
  isEnded,
}: ScoreboardV2Props): Node => {
  const hasAtLeastOneCompetitor = useMemo(
    () => MatchEntityCompetitors.hasAtLeastOneCompetitor(competitors),
    [competitors],
  );
  const hasAtLeastTwoCompetitors = useMemo(
    () => MatchEntityCompetitors.hasAtLeastTwoCompetitors(competitors),
    [competitors],
  );
  const hasScorer = useMemo(
    () => MatchEntityCompetitors.hasScorer(competitors, hasAtLeastOneCompetitor, hasAtLeastTwoCompetitors),
    [competitors, hasAtLeastOneCompetitor, hasAtLeastTwoCompetitors],
  );

  const renderSecondaryScore = (index, label, score, isActive, isPso) => {
    if (isPso) {
      return <StyledPsoScore score1={score[0]} score2={score[1]} />;
    }

    return (
      <SecondaryScore key={index} isActive={isActive ?? false}>
        {`${label || ''} : ${score[0]} - ${score[1]}`}
      </SecondaryScore>
    );
  };

  const renderScorers = useCallback(
    (competitors) => {
      const scorersCompetitor1 = (hasAtLeastOneCompetitor && competitors[0].scorers) || [];
      const scorersCompetitor2 = (hasAtLeastTwoCompetitors && competitors[1].scorers) || [];
      return (
        <ScoreboardScorers
          scorersCompetitor1={scorersCompetitor1}
          scorersCompetitor2={scorersCompetitor2}
          expandThreshold={expandThreshold}
          sportId={sportId}
        />
      );
    },
    [hasAtLeastOneCompetitor, hasAtLeastTwoCompetitors, expandThreshold, sportId],
  );

  return (
    <Wrapper className={className}>
      <Main>
        <MatchTitle
          competitor1Name={hasAtLeastOneCompetitor ? competitors[0].name : ''}
          competitor2Name={hasAtLeastTwoCompetitors ? competitors[1].name : ''}
          competitor1NbRedCards={hasAtLeastOneCompetitor ? competitors[0].nbRedCards : 0}
          competitor2NbRedCards={hasAtLeastTwoCompetitors ? competitors[1].nbRedCards : 0}
          score1={
            scores?.main?.score?.length >= 1 && isNumber(scores.main.score[0])
              ? parseInt(scores.main.score[0], 10)
              : null
          }
          score2={
            scores?.main?.score?.length >= 2 && isNumber(scores.main.score[1])
              ? parseInt(scores.main.score[1], 10)
              : null
          }
          flag1={hasAtLeastOneCompetitor ? competitors[0].flag : undefined}
          flag2={hasAtLeastTwoCompetitors ? competitors[1].flag : undefined}
          animatedScore={MatchTitleAllowedSportIdsAnimation.includes(sportId)}
          isFinalScore={isEnded}
        />

        {!isCompact ? (
          <RowWrapper>
            <CenterColumn>
              <ScoreArea>
                {scores?.secondaries?.map(({ label, score, isActive, isPso }, index) =>
                  renderSecondaryScore(index, label, score, isActive, isPso),
                )}
              </ScoreArea>
            </CenterColumn>
          </RowWrapper>
        ) : null}
      </Main>
      {!isCompact && hasScorer ? renderScorers(competitors) : null}
    </Wrapper>
  );
};

export default ScoreboardDefaultLayout;
